import React from 'react';
import { Box } from '@mui/material';
import { useSuperadminAuth } from '../../../contexts/SuperadminAuthContext';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { styled } from '@mui/material/styles';
import { Typography, Input } from '@/components/ui';

// Styled components for profile update
const ProfileContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: '0 auto',
  padding: theme.spacing(4),
  backgroundColor: 'white',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
}));

const ProfileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
}));

const FormSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
}));

// const ButtonGroup = styled(Box)(({ theme }) => ({
//   display: 'flex',
//   gap: theme.spacing(2),
//   justifyContent: 'flex-end',
//   marginTop: theme.spacing(3),
// }));

// Interface for profile update request - commented out since updates are not allowed
// interface UpdateProfileRequest {
//   email: string;
// }

// Validation schema for profile update
const profileSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
});

// Get user object from localStorage (parse JSON if exists)
const getLocalUser = () => {
  const userStr = localStorage.getItem('superadmin_auth_user');
  try {
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

// Initial form values
const getInitialValues = (user: any) => {
  const localUser = getLocalUser();
  const localEmail = localStorage.getItem('email');
  
  return {
    email: localEmail || localUser?.email || user?.email || '',
  };
};

export const UpdateProfileForm: React.FC = () => {
  const { user } = useSuperadminAuth(); // Only need user data for display

  // Form submission handler removed since profile updates are not allowed
  const handleSubmit = async () => {
    // No-op since profile updates are disabled
  };

  if (!user) {
    return null; // Don't render if user is not available
  }

  return (
    <ProfileContainer>
      {/* Profile Header */}
      <ProfileHeader>
        <Typography
          variant="h4"
          weight="bold"
          color="textPrimary"
          sx={{ marginBottom: 1 }}
        >
          Profile Information
        </Typography>
        <Typography variant="body2" color="textSecondary">
          View your superadmin profile information
        </Typography>
      </ProfileHeader>

      {/* Profile Form */}
      <Formik
        initialValues={getInitialValues(user)}
        validationSchema={profileSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form>
            <FormSection>
              {/* Email Field */}
              <Box>
                <Input
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={values.email || ''}
                  disabled={true} // Email is read-only - not editable
                  size="large"
                  variant="outlined"
                />
              </Box>

              {/* User Info Display (Read-only) */}
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  User Information (Read-only)
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {user.name || 'Admin User'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Role:</strong> {user.role || 'superadmin'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>UUID:</strong> {user.id || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </FormSection>

            {/* Action Buttons - Commented out as profile updates are not allowed */}
            {/* <ButtonGroup>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/superadmin/dashboard')}
                disabled={loading || isSubmitting}
                sx={{ px: 4 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="large"
                loading={loading || isSubmitting}
                loadingText="Updating profile..."
                disabled={!values.email || loading || isSubmitting}
                sx={{ px: 4 }}
              >
                Update Profile
              </Button>
            </ButtonGroup> */}
          </Form>
        )}
      </Formik>
    </ProfileContainer>
  );
};

export default UpdateProfileForm;
